import { useEffect, useState, ReactNode } from "react";
import { Button, Drawer, Modal, Skeleton, message } from "antd";
import { useTranslation } from "react-i18next";
import { authRequest } from "../../../../../common/request/authRequest";
import { ButtonLZ } from "../../../../components/design/Buttons/ButtonLZ";
import { NewKeywordModal } from "./NewKeywordModal";
import { DeleteOutlined, DownOutlined, FolderOpenOutlined, GlobalOutlined, PlusOutlined, ShopOutlined, UpOutlined } from "@ant-design/icons";
import { authFetch } from "../../../../../common/request/authFetch";
import { Batch } from "../../../menuUsers/keywords/entity/TermUtils";
import { useAuth } from "../../../../../store/AuthContext/AuthContext";
import { DrawerPanelTitle, drawerPanelStyles } from "../../../common/Drawers/DrawerPanelTitle";
import { PageInfoCard } from "../../../common/PageInfoCard";
import { KeywordResultKeyword } from "../../pageCompetitors/_common/GridComponents";
import "../../../common/PageTitle.css";

type KeywordScope = 'DOMAIN' | 'GROUP' | 'LOCAL';

const KEYWORDS_DRAWER_INFO = `Aquí defines las keywords que quieres monitorizar con el ranking tracker de SEO local. Puedes organizarlas en tres niveles según el alcance:

- **Dominio**: aplican a todos los locales que compartan el mismo dominio web.
- **Grupo**: aplican a todas las ubicaciones del grupo de Google indicado.
- **Local**: aplican solo a este establecimiento.`;

const KeywordSection = (props: {
	icon: ReactNode;
	title: string;
	subtitle?: string;
	keywords: any[] | undefined;
	scope: KeywordScope;
	addLabel: string;
	onAdd: () => void;
	onDelete: (term: string, scope: KeywordScope) => void;
	onBatchFinish: () => void;
	confirmLabel: string;
	cancelLabel: string;
}) => {
	const hasKeywords = (props.keywords?.length ?? 0) > 0;

	return (
		<section style={{
			background: '#ffffff',
			border: '1px solid #e2e8f0',
			borderRadius: 12,
			padding: '14px 16px 12px',
			marginBottom: 14,
		}}>
			<div style={{
				marginBottom: 12,
				paddingBottom: 10,
				borderBottom: '1px solid #f1f5f9',
			}}>
				<div style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 8,
					fontSize: 14,
					fontWeight: 700,
					color: '#0f172a',
					lineHeight: 1.3,
				}}>
					<span style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 26,
						height: 26,
						borderRadius: 7,
						background: '#f0fdfa',
						color: '#0f766e',
						flexShrink: 0,
					}}>
						{props.icon}
					</span>
					{props.title}
				</div>
				{props.subtitle && (
					<div style={{
						marginTop: 4,
						marginLeft: 34,
						fontSize: 12,
						color: '#64748b',
						fontWeight: 500,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}>
						{props.subtitle}
					</div>
				)}
			</div>

			{hasKeywords ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					{props.keywords!.map((kw: any) => (
						<div
							key={kw.term}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 12,
								padding: '8px 10px',
								border: '1px solid #e2e8f0',
								borderRadius: 8,
								background: '#fafbfc',
							}}
						>
							<div style={{
								display: 'flex',
								alignItems: 'center',
								gap: 12,
								minWidth: 0,
								flex: 1,
							}}>
								<div style={{ width: 220, flexShrink: 0 }}>
									<KeywordResultKeyword keyword={kw.term} scope={props.scope} />
								</div>
								<Batch kw={kw} onFinish={props.onBatchFinish} />
							</div>
							<Button
								shape="round"
								size="small"
								type="text"
								icon={<DeleteOutlined style={{ color: '#94a3b8' }} />}
								onClick={() => {
									Modal.confirm({
										title: 'Confirmas que quieres eliminar esta keyword',
										okText: props.confirmLabel,
										cancelText: props.cancelLabel,
										onOk: () => props.onDelete(kw.term, props.scope),
									});
								}}
							/>
						</div>
					))}
				</div>
			) : (
				<div style={{
					padding: '18px 12px',
					textAlign: 'center',
					border: '1.5px dashed #cbd5e1',
					borderRadius: 8,
					background: '#f8fafc',
					color: '#94a3b8',
					fontSize: 13,
					fontWeight: 500,
				}}>
					Aún no hay keywords definidas
				</div>
			)}

			<div style={{
				display: 'flex',
				justifyContent: 'flex-end',
				marginTop: 12,
			}}>
				<ButtonLZ onClick={props.onAdd} size="small" icon={<PlusOutlined />}>
					{props.addLabel}
				</ButtonLZ>
			</div>
		</section>
	);
};

export const KeywordsDrawer = (props: { location: any, onFinish: any, onRefresh: any }) => {
	const [data, setData] = useState<any>({});
	const [updateData, setUpdateData] = useState(false);
	const [openModal, setOpenModal] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [infoExpanded, setInfoExpanded] = useState(false);
	const { projectGroup } = useAuth();
	const { t } = useTranslation();

	useEffect(() => {
		setIsLoading(true);
		authRequest(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/keywords/${props.location.composeId}`,
			{ method: 'GET' }
		)
			.then((data) => {
				setData(data);
				setIsLoading(false);
			})
			.catch(() => {
				message.error(t('view.errors.loadLocation'));
			});
	}, [updateData]);

	const deleteReply = async (term: string, scope: string): Promise<void> => {
		const payload = {
			locationId: props.location.composeId,
			websiteUri: props.location.websiteUri,
			keyword: term,
			scope: scope,
		};

		await authFetch(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/keywords/${props.location.composeId}/terms`,
			{ method: 'DELETE', data: payload }
		).then((res) => {
			if (res) {
				message.success('Keyword borrada de forma satisfactoria', 3);
				setUpdateData(!updateData);
				props.onFinish();
			} else {
				message.error('Problema al borrar la keyword', 3);
			}
		});
	};

	const onBatchFinish = () => {
		props.onRefresh();
		setUpdateData(!updateData);
	};

	return (
		<Drawer
			placement="right"
			width={'50%'}
			open={true}
			onClose={props.onFinish}
			{...drawerPanelStyles}
			title={
				<DrawerPanelTitle
					title={
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
							Keywords
							<button
								type="button"
								className="page-info-icon"
								aria-label={infoExpanded ? 'Ocultar información' : 'Mostrar información'}
								aria-expanded={infoExpanded}
								onClick={(e) => {
									e.stopPropagation();
									setInfoExpanded((prev) => !prev);
								}}
							>
								{infoExpanded ? <UpOutlined /> : <DownOutlined />}
							</button>
						</span>
					}
					location={props.location}
				/>
			}
		>
			{infoExpanded && (
				<div style={{ marginBottom: 14 }}>
					<PageInfoCard
						title="Sobre las keywords"
						description={KEYWORDS_DRAWER_INFO}
					/>
				</div>
			)}
			{isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
			{!isLoading && (
				<div>
					{data?.domain && (
						<KeywordSection
							icon={<GlobalOutlined />}
							title="Keywords de dominio"
							subtitle={data.domain}
							keywords={data.domainKeywords}
							scope="DOMAIN"
							addLabel="keyword de dominio"
							onAdd={() => setOpenModal('DOMAIN')}
							onDelete={deleteReply}
							onBatchFinish={onBatchFinish}
							confirmLabel={t('dictionary.confirm')}
							cancelLabel={t('dictionary.cancel')}
						/>
					)}

					<KeywordSection
						icon={<FolderOpenOutlined />}
						title="Para todo el grupo de ubicaciones"
						subtitle={data?.groupDisplayName}
						keywords={data?.groupKeywords}
						scope="GROUP"
						addLabel="keyword de grupo"
						onAdd={() => setOpenModal('GROUP')}
						onDelete={deleteReply}
						onBatchFinish={onBatchFinish}
						confirmLabel={t('dictionary.confirm')}
						cancelLabel={t('dictionary.cancel')}
					/>

					<KeywordSection
						icon={<ShopOutlined />}
						title="Para este local"
						subtitle={props.location?.title}
						keywords={data?.locationKeywords}
						scope="LOCAL"
						addLabel="keyword de local"
						onAdd={() => setOpenModal('LOCAL')}
						onDelete={deleteReply}
						onBatchFinish={onBatchFinish}
						confirmLabel={t('dictionary.confirm')}
						cancelLabel={t('dictionary.cancel')}
					/>
				</div>
			)}

			{openModal && (
				<NewKeywordModal
					projectId={projectGroup.project}
					composeId={props.location.composeId}
					websiteUri={props.location.websiteUri}
					scope={openModal}
					onFinish={() => {
						setUpdateData(!updateData);
						props.onRefresh();
						setOpenModal(null);
					}}
				/>
			)}
		</Drawer>
	);
};

export const DAYS: any = {
	"0": " Domingo",
	"1": " Lunes",
	"2": " Martes",
	"3": " Miércoles",
	"4": " Jueves",
	"5": " Viernes",
	"6": " Sábado",
};
